import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { useTranslation } from 'react-i18next';
import { ListOptions } from "../../models/interfaces/list-options";
import { DateTime } from "luxon";
import { Group } from "../../models/interfaces/group";
import { Tag } from "../../models/interfaces/tag";
import ReportDataUtil from "../../utilities/report-data-util";
import Select from "react-select";
import { FilterState } from "../../models/interfaces/filter-state";
import { ReportDynamicSummaryData } from "../../models/interfaces/reports/report-dynamic-summary-data";
import PillChartArray from "../charts/pill-chart/pill-chart-array";
import GroupUtil from "../../utilities/group-util";
import { Button } from "../button/button";
import { Loader } from "../loader/loader";
import ReportFilterMenu from "../report-filter-menu/report-filter-menu";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import React from "react";



const COMPONENT_CLASS = "c-report-summary-dynamic";

const DEFAULT_FILTERS: FilterState = {
    groupIds: [],
    tagIds: [],
};

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportSummaryDynamic: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const { state } = useAuthState();
    const reportMonthOptions = useMemo<ListOptions[]>(() => {
        const options: ListOptions[] = [
            {
                value: DateTime.now().startOf("month").toISODate()!,
                label: t('reports.currentMonth')
            }
        ];

        if (!state.organization?.created) {
            return options;
        }

        let monthDate = DateTime.now().minus({ month: 1 });

        const orgCreatedDate = DateTime
            .fromJSDate(new Date(state.organization.created.seconds * 1000))
            .startOf('month');

        const monthDifference = DateTime.now()
            .startOf('month')
            .diff(orgCreatedDate, 'months')
            .months;

        while (options.length <= monthDifference) {
            options.push({
                value: monthDate.startOf("month").toISODate()!,
                label: monthDate
                    .setLocale(i18n.language)
                    .toFormat("MMMM yyyy"),
            });

            monthDate = monthDate.minus({ month: 1 });
        }

        return options;

    }, [t, state.organization.created, i18n.language]);
    const [reportData, setReportData] = useState<ReportDynamicSummaryData | null>(null);
    const [filteredReportData, setFilteredReportData] = useState<ReportDynamicSummaryData | null>(null);
    const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [selectedReportMonth, setSelectedReportMonth] = useState<string>(
        reportMonthOptions[0]?.value
    );
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const riskSummaries = [{
        name: "Health",
        displayAs: t('reports.summary.riskSummaries.health.displayAs'),
        description: t('reports.summary.riskSummaries.health.description'),
        color: "bg-red-light"
    }, {
        name: "Wellness",
        displayAs: t('reports.summary.riskSummaries.wellness.displayAs'),
        description: t('reports.summary.riskSummaries.wellness.description'),
        color: "bg-yellow-light"
    }, {
        name: "Fitness",
        displayAs: t('reports.summary.riskSummaries.fitness.displayAs'),
        description: t('reports.summary.riskSummaries.fitness.description'),
        color: "bg-blue-light"
    }, {
        name: "Performance",
        displayAs: t('reports.summary.riskSummaries.performance.displayAs'),
        description: t('reports.summary.riskSummaries.performance.description'),
        color: "bg-green-light"
    }]

    const filterDefinitions: ReportFilterDefinition[] = useMemo(() => [
        {
            key: "groupIds",
            label: t('reports.filterOptions.groups'),
            placeholder: t('reports.filterOptions.allGroups'),
            type: ReportFilterType.MultiSelect,
            options: groups.map(g => ({ label: g.name, value: g.id }))
        },
        {
            key: "tagIds",
            label: t('reports.filterOptions.tags'),
            placeholder: t('reports.filterOptions.allTags'),
            type: ReportFilterType.MultiSelect,
            options: tags.map(t => ({ label: t.name, value: t.id }))
        },
    ], [groups, tags, t]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        const load = async () => {
            const { groups, tags } = await ReportDataUtil.loadGroupsAndTags(orgId);
            setGroups(groups);
            setTags(tags);
        };

        load();
    }, [state.organization?.id]);

    useEffect(() => {
        if (!reportData) return;

        const filtered = applyFilters(reportData, filters);
        setFilteredReportData(filtered);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, reportData]);
    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId || !selectedReportMonth) return;

        // Clear existing data immediately
        setReportData(null);
        setFilteredReportData(null);

        // reset UI state
        setFilters(DEFAULT_FILTERS);

        const loadData = async () => {

            const data = await ReportDataUtil.grabInitialDynamicSummaryData(orgId, selectedReportMonth);
            setReportData(data);
            setFilteredReportData(data);
        };

        loadData();
    }, [selectedReportMonth, state.organization?.id]);

    useEffect(() => {
        if (reportData) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
        }
    }, [reportData])

    const applyFilters = (
        reportData: ReportDynamicSummaryData,
        filters: FilterState,
    ): ReportDynamicSummaryData => {
        return ReportDataUtil.filterInitialDynamicSummaryData(reportData, filters, selectedReportMonth);
    };

    const handleExportCsv = () => {
        if (!filteredReportData) return;

        const csvString = ReportDataUtil.generateSummary({
            focusAreas: filteredReportData.focusAreas,
            history: filteredReportData.history,
            organizationName: filteredReportData.organizationName,
            pendingUsers: filteredReportData.pendingUsers,
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            riskFactorCodeCounts: filteredReportData.riskFactorCodeCounts,
            scoreBreakdown: filteredReportData.scoreBreakdown,
            movementFocusAreas: filteredReportData.movementFocusAreas,
            totalUsers: filteredReportData.totalUsers,
        });

        const blob = new Blob(
            ["\uFEFF" + csvString], // UTF-8 BOM for Excel
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${t('reports.summary.fileReportName')}_${selectedReportMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />
            <div className={`${COMPONENT_CLASS}__header-container`}>
                <div className={`${COMPONENT_CLASS}__header`}>
                    <h1>{t('reportType.summary_rep')}</h1>
                    <p className={`label-large ${COMPONENT_CLASS}__sub-header`}>{state.organization?.name} • {selectedReportMonth} • {filteredReportData?.users.length} {t('general.users')}</p>
                </div>
                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button
                        onClick={handleExportCsv}
                        buttonText={t('buttons.btn_exportCSV')}
                        type="link" />
                    <Button
                        disabled={!filteredReportData}
                        onClick={() => ReportDataUtil.downloadSummary({
                            groupNames: filteredReportData?.groupNames,
                            tagNames: filteredReportData?.tagNames,
                            focusAreas: filteredReportData!.focusAreas,
                            history: filteredReportData!.history,
                            organizationName: filteredReportData!.organizationName,
                            pendingUsers: filteredReportData!.pendingUsers,
                            riskFactorCodeCounts: filteredReportData!.riskFactorCodeCounts,
                            scoreBreakdown: filteredReportData!.scoreBreakdown,
                            movementFocusAreas: filteredReportData!.movementFocusAreas,
                            totalUsers: filteredReportData!.totalUsers,
                            reportDate: selectedReportMonth
                        },
                            ReportDataUtil.buildReportFilterPayload(groups, tags, filters),
                            state.organization?.id!,
                            reportData?.organizationName ?? '',
                            state.user?.id!,
                            selectedReportMonth!)}
                        buttonText={t('buttons.btn_exportPDF')}
                        type="link" />
                    <ReportFilterMenu
                        filters={filterDefinitions}
                        values={filters}
                        onChange={(key, value) =>
                            setFilters(prev => ({
                                ...prev,
                                [key]: value
                            }))
                        }
                    >
                        <label className={`${COMPONENT_CLASS}__label body-bold`}>
                            {t('reports.filterOptions.reportDate')}
                        </label>
                        <Select
                            name="dateEnd"
                            classNamePrefix={COMPONENT_CLASS}
                            placeholder={t('reports.filterOptions.reportDate')}
                            options={reportMonthOptions}
                            value={reportMonthOptions.find(o => o.value === selectedReportMonth)}
                            onChange={(option: any) => {
                                if (!option) return;
                                setSelectedReportMonth(option.value);
                            }}
                        />
                    </ReportFilterMenu>
                </div>
            </div>
            {
                filteredReportData && (
                    <div className={`${COMPONENT_CLASS}__data-container`}>
                        <div className={`${COMPONENT_CLASS}__risk-breakdown`}>
                            <div className={`${COMPONENT_CLASS}__risk-items`}>
                                {filteredReportData.riskFactorCodeCounts.map((risk, index) => (
                                    <div
                                        className={`${COMPONENT_CLASS}__risk-item ${risk.name.toLowerCase()} ${riskSummaries.find((r) => r.name === risk.name)?.color}`}
                                        key={`risk-${index}`}>
                                        <div className={`${COMPONENT_CLASS}__risk-item__count data-small`}>{risk.count}</div>
                                        <div className={`${COMPONENT_CLASS}__risk-item__name body-bold`}>
                                            <p>
                                                {riskSummaries.find((r) => r.name === risk.name)?.displayAs ?? risk.name}
                                            </p>
                                        </div>
                                        <div className={`${COMPONENT_CLASS}__risk-item__description small-text`}>
                                            <p>
                                                {riskSummaries.find((r) => r.name === risk.name)?.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__wellness-history`}>
                            <h2>{t('reports.summary.wellnessHistory')}</h2>
                            <div className={`${COMPONENT_CLASS}__history-items`}>
                                {filteredReportData.history.map((item, index) => {
                                    let className = `${COMPONENT_CLASS}__history-item `;
                                    const date = DateTime.fromISO(item.date);
                                    if (index === 0 && filteredReportData.history.length > 2) {
                                        const diff = item.percent - filteredReportData.history[1].percent;
                                        className += diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral";
                                    }
                                    if (item.percent <= 0) {
                                        className += " not-available";
                                    }
                                    if (index === 0) {
                                        className += " first-history-item";
                                    }

                                    return (
                                        <div
                                            className={className}
                                            key={`history-${index}`}>
                                            <div className={`${COMPONENT_CLASS}__history-item__score data-small`}>{item.percent > 0 ? item.percent : t('general.notApplicable')}</div>
                                            <div className={`${COMPONENT_CLASS}__history-item__date body`}>{index === 0 && date.diffNow("month").months > -1 ? t('reports.summary.currentLowerCase') : date.setLocale(i18n.language).toFormat("MMM")}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__score-breakdown`}>
                            <h2>{t('dashboard.currentScoreBreak')}</h2>
                            <div style={{ marginTop: "1rem" }}>
                                <PillChartArray
                                    forReport
                                    noTitle
                                    data={filteredReportData?.scoreBreakdown.map((score) => ({ range: `${score.name}`, value: score.count }))}
                                />
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__focus-areas`}>
                            <div className={`${COMPONENT_CLASS}__focus-areas-section`}>
                                <h2>{t('reports.summary.focusAreas')}</h2>
                                <div className={`${COMPONENT_CLASS}__focus-items`}>
                                    {filteredReportData.focusAreas.map((focus, index) => (
                                        <React.Fragment key={index}>
                                            <div className={`${COMPONENT_CLASS}__focus-item__count`}>
                                                <h2>{focus.count}</h2>
                                            </div>
                                            <div className={`${COMPONENT_CLASS}__focus-item__name body`}>
                                                {t(GroupUtil.getNameTranslationReference(focus.groupId))}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                            <div className={`${COMPONENT_CLASS}__movement-focus-areas-section`}>
                                <h2>{t('reports.summary.movementFocus')}</h2>
                                <div className={`${COMPONENT_CLASS}__focus-items`}>
                                    {filteredReportData.movementFocusAreas.map((focus, index) => (
                                        <React.Fragment key={index}>
                                            <div className={`${COMPONENT_CLASS}__focus-item__count`}>
                                                <h2>{focus.count}</h2>
                                            </div>
                                            <div className={`${COMPONENT_CLASS}__focus-item__name body`}>
                                                {t(GroupUtil.getNameTranslationReference(focus.groupId))}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                )
            }


        </div>
    );
}


export default ReportSummaryDynamic;
